"use client";

import React from "react";
import { Bath, Grid2x2 as Grid2X2, HeartHandshake, Layers, Lightbulb, Sparkles, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  id: "showers-tubs",
  title: "Walk-in showers & tubs",
  icon: "Bath",
  description: "Curbless zero-threshold entries, frameless fluted or ultra-clear glass, custom bench seating, dual thermostatic rainfall heads, and standalone soaking tubs.",
  highlights: "Zero-threshold curbless entry · Freestanding soaking tubs · Thermostatic dual valves"
}, {
  id: "vanities-millwork",
  title: "Custom vanities & storage",
  icon: "Layers",
  description: "Furniture-grade rift-sawn white oak or tailored painted millwork, soft-close dovetailed drawers, custom dividers, and premium quartz or honed quartzite tops.",
  highlights: "Rift-sawn white oak · Dovetailed joinery · Honed quartzite surfaces"
}, {
  id: "tile-flooring",
  title: "Tile & heated flooring",
  icon: "Grid2X2",
  description: "Precision-mitered porcelain slabs, handmade zellige wall accents, marble herringbone floor patterns, and programmable in-floor radiant heating systems.",
  highlights: "Radiant in-floor heating · Precision mitered slab edges · Handcrafted zellige"
}, {
  id: "lighting-ventilation",
  title: "Lighting & quiet ventilation",
  icon: "Lightbulb",
  description: "Multi-zone architectural illumination with perimeter cove accents, dimmable designer vanity sconces, backlit mirrors, and whisper-quiet moisture exhaust.",
  highlights: "Perimeter architectural coves · Dimmable vanity sconces · Whisper exhaust"
}, {
  id: "fixtures-hardware",
  title: "Fixtures & solid hardware",
  icon: "Sparkles",
  description: "Solid forged brass hardware in unlacquered or brushed gold finishes, anti-scald digital valves, and high-efficiency skirted or wall-hung water closets.",
  highlights: "Solid forged brass · Unlacquered & brushed gold · Concealed tank systems"
}, {
  id: "accessibility-design",
  title: "Accessibility & aging-in-place",
  icon: "HeartHandshake",
  description: "Discreet structural wall backing, matching designer grab bars, barrier-free transitions, and ergonomic lever controls built for lasting independence.",
  highlights: "Concealed structural backing · Finish-matched grab bars · Level-entry paths"
}];
export default function Included() {
  const shouldReduceMotion = useReducedMotion();
  const getFeatureIcon = (iconName: string) => {
    switch (iconName) {
      case "Bath":
        return <Bath className="size-5 text-primary" strokeWidth={1.75} />;
      case "Layers":
        return <Layers className="size-5 text-primary" strokeWidth={1.75} />;
      case "Grid2X2":
        return <Grid2X2 className="size-5 text-primary" strokeWidth={1.75} />;
      case "Lightbulb":
        return <Lightbulb className="size-5 text-primary" strokeWidth={1.75} />;
      case "Sparkles":
        return <Sparkles className="size-5 text-primary" strokeWidth={1.75} />;
      case "HeartHandshake":
        return <HeartHandshake className="size-5 text-primary" strokeWidth={1.75} />;
      default:
        return <Sparkles className="size-5 text-primary" strokeWidth={1.75} />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="whats-included" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3.5 py-1 text-xs tracking-widest uppercase font-semibold text-primary border-primary/30 bg-primary/10 rounded-full">
            Comprehensive remodeling scope
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-[1.2] mb-5">
            Every detail of your Brookhaven master suite, mastered
          </h2>

          <div className="h-px bg-primary/40 w-24 mx-auto mb-6" />

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans max-w-2xl mx-auto">
            From curbless entry showers to custom rift-sawn oak millwork, every fixture, surface, and system is designed, permitted, and built by our dedicated Brookhaven team.
          </p>
        </div>

        {/* Feature Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((item, i) => <motion.div key={item.id} data-index={i} variants={cardVariants} whileHover={shouldReduceMotion ? undefined : {
          y: -4,
          transition: {
            duration: 0.25,
            ease: "easeOut"
          }
        }} className="group relative flex flex-col justify-between rounded-2xl p-7 sm:p-8 bg-card/80 backdrop-blur-md border border-border/80 shadow-xs hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow] duration-300">
              <div>
                {/* Icon Badge */}
                <div className="size-12 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mb-6 group-hover:bg-primary/15 transition-colors duration-300">
                  {getFeatureIcon(item.icon)}
                </div>

                {/* Card Title */}
                <h3 className="font-sans font-semibold text-xl text-foreground tracking-tight mb-3">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-muted-foreground text-sm leading-relaxed mb-6 font-sans">
                  {item.description}
                </p>

                {/* Micro Highlights Pill */}
                <div className="pt-4 border-t border-border/60 mb-6">
                  <span className="text-xs font-medium text-foreground/80 tracking-wide uppercase">
                    Key specifications
                  </span>
                  <p className="text-xs text-muted-foreground mt-1 leading-normal font-sans">
                    {item.highlights}
                  </p>
                </div>
              </div>

              {/* Action Link */}
              <div className="pt-2">
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors">
                  <span>Request material specs</span>
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom Context Callout */}
        <div className="mt-14 md:mt-20 p-6 md:p-8 rounded-2xl bg-card border border-border/70 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center md:text-left">
            <h4 className="font-sans font-semibold text-lg text-foreground mb-1">
              Have specific architectural plans or plumbing requirements?
            </h4>
            <p className="text-sm text-muted-foreground">
              We coordinate all city permitting and structural assessments across Brookhaven, Dunwoody, and Buckhead before demolition begins.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-lg shrink-0">
            <Link to="/ContactRequestAnEstimatePage">
              Consult with our builders
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}